<template>
  <div class="row wrapper border-bottom white-bg page-heading">
    <div class="col-lg-10">
      <h2>{{ isEdit ? "Edytuj koszt stały" : "Dodaj koszt stały" }}</h2>
      <ol class="breadcrumb">
        <li>
          <router-link to="/">Home</router-link>
        </li>
        <li>
          <router-link to="/costs">Baza kosztów stałych</router-link>
        </li>
        <li class="active">
          <strong>{{
            isEdit ? "Edytuj koszt stały" : "Dodaj koszt stały"
          }}</strong>
        </li>
      </ol>
    </div>
  </div>

  <div class="wrapper wrapper-content animated fadeInRight">
    <div class="row">
      <div class="col-lg-12">
        <div class="ibox float-e-margins">
          <div class="ibox-title">
            <h5>Dane kosztu stałego</h5>
          </div>
          <div class="form-horizontal">
            <div class="ibox-content">
              <div class="form-group">
                <label class="control-label col-sm-2">
                  Nazwa kosztu <span class="text-danger">*</span>
                </label>
                <div class="col-sm-10">
                  <input
                    v-model="costObj.name"
                    type="text"
                    class="form-control"
                    placeholder="Nazwa kosztu"
                  />
                  <div v-if="errorsAdminData?.data?.name" class="has-error">
                    <span class="help-block">
                      {{ errorsAdminData.data.name[0] }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="form-group">
                <label class="control-label col-sm-2">
                  Wartość <span class="text-danger">*</span>
                </label>
                <div class="col-sm-10">
                  <input
                    v-model="costObj.value"
                    type="number"
                    step="0.01"
                    class="form-control"
                    placeholder="Wartość"
                  />
                  <div v-if="errorsAdminData?.data?.value" class="has-error">
                    <span class="help-block">
                      {{ errorsAdminData.data.value[0] }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="form-group">
                <label class="control-label col-sm-2">
                  Jednostka <span class="text-danger">*</span>
                </label>
                <div class="col-sm-10">
                  <Multiselect
                    v-model="costObj.unit"
                    mode="single"
                    placeholder="Wybierz jednostkę"
                    :close-on-select="true"
                    :searchable="true"
                    :options="unitsOptions"
                    label="name"
                    track-by="value"
                  >
                  </Multiselect>
                  <div v-if="errorsAdminData?.data?.unit" class="has-error">
                    <span class="help-block">
                      {{ errorsAdminData.data.unit[0] }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="form-group">
                <label class="control-label col-sm-2">
                  Wartość okresu <span class="text-danger">*</span>
                </label>
                <div class="col-sm-10">
                  <input
                    v-model="costObj.period"
                    type="number"
                    class="form-control"
                    placeholder="Wartość okresu"
                  />
                  <div v-if="errorsAdminData?.data?.period" class="has-error">
                    <span class="help-block">
                      {{ errorsAdminData.data.period[0] }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="form-group">
                <label class="control-label col-sm-2">
                  Jednostka okresu <span class="text-danger">*</span>
                </label>
                <div class="col-sm-10">
                  <Multiselect
                    v-model="costObj.period_unit"
                    mode="single"
                    placeholder="Wybierz jednostkę okresu"
                    :close-on-select="true"
                    :searchable="true"
                    :options="periodUnitOptions"
                    label="name"
                    track-by="value"
                  >
                  </Multiselect>
                  <div
                    v-if="errorsAdminData?.data?.period_unit"
                    class="has-error"
                  >
                    <span class="help-block">
                      {{ errorsAdminData.data.period_unit[0] }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="hr-line-dashed"></div>

              <div class="form-group">
                <div class="col-sm-4 col-sm-offset-2">
                  <button
                    v-if="canSaveCost"
                    @click="saveCost"
                    class="btn btn-primary"
                    :disabled="loading"
                  >
                    {{
                      loading
                        ? "Zapisywanie..."
                        : isEdit
                        ? "Zapisz zmiany"
                        : "Dodaj"
                    }}
                  </button>
                  <router-link to="/costs" class="btn btn-white">
                    Anuluj
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { hasPermission } from "../../permissions";
import { costPermissionsConst } from "../../constants/costConstants";
import { useRoute, useRouter } from "vue-router";
import Multiselect from "@vueform/multiselect";

export default {
  name: "CostsCreate",
  mixins: [],
  components: {
    Multiselect,
  },
  props: {},

  setup() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();

    const allCostsData = computed(() => store.getters.getCostData);
    const allCostsCreateData = computed(
      () => store.getters.getAllCostsCreateData
    );
    const allCostsUpdateData = computed(
      () => store.getters.getAllCostsUpdateData
    );
    const unitsData = computed(() => store.getters.getUnitsData);
    const periodUnitOptsData = computed(
      () => store.getters.getPeriodUnitOptsData
    );
    const errorsAdminData = computed(() => store.getters.getErrorsAdminData);

    const isEdit = ref(false);
    // The same form creates and edits a cost.
    const canSaveCost = computed(() =>
      hasPermission(
        isEdit.value ? costPermissionsConst.update : costPermissionsConst.add
      )
    );
    const loading = ref(false);
    const costObj = ref({
      name: "",
      value: "",
      unit: null,
      period: "",
      period_unit: null,
    });

    const unitsOptions = computed(() => {
      const data =
        unitsData.value?.data?.results || unitsData.value?.data || [];

      if (!Array.isArray(data)) {
        return [];
      }

      return data.map((unit) => ({
        name: unit.name,
        value: unit.pk,
      }));
    });

    const periodUnitOptions = computed(() => {
      const data =
        periodUnitOptsData.value?.data?.data ||
        periodUnitOptsData.value?.data ||
        [];

      if (typeof data === "string") {
        try {
          const parsed = JSON.parse(data);

          // Sprawdź czy parsed jest tablicą
          if (!Array.isArray(parsed)) {
            return [];
          }

          return parsed.map((option) => ({
            name: option.name,
            value: option.value,
          }));
        } catch (e) {
          console.error("Error parsing periodUnitOpts data:", e);
          return [];
        }
      }

      if (!Array.isArray(data)) {
        return [];
      }

      return data;
    });

    const saveCost = async () => {
      if (!canSaveCost.value) {
        return;
      }
      loading.value = true;
      try {
        const payload = {
          name: costObj.value.name,
          value: parseFloat(costObj.value.value),
          unit: costObj.value.unit?.value || costObj.value.unit,
          period: parseInt(costObj.value.period),
          period_unit:
            costObj.value.period_unit?.value || costObj.value.period_unit,
        };

        if (isEdit.value) {
          await store.dispatch("updateCost", {
            pk: route.params.pk,
            data: payload,
          });
          router.push({ path: "/costs", query: { r: Date.now() } }).then(() => {
            store.dispatch("showToaster", {
              type: "success",
              content: "Koszt stały został zaktualizowany",
            });
          });
        } else {
          await store.dispatch("createCost", payload);
          router.push({ path: "/costs", query: { r: Date.now() } }).then(() => {
            store.dispatch("showToaster", {
              type: "success",
              content: "Koszt stały został dodany",
            });
          });
        }
      } catch (error) {
        console.error("Error saving cost:", error);
        store.dispatch("showToaster", {
          type: "error",
          content: "Błąd podczas zapisu kosztu stałego",
        });
      } finally {
        loading.value = false;
      }
    };

    const loadCostData = async () => {
      if (isEdit.value) {
        try {
          await store.dispatch("fetchCost", route.params.pk);
        } catch (error) {
          console.error("Error loading cost data:", error);
        }
      }
    };

    const loadReferenceData = async () => {
      try {
        await store.dispatch("fetchUnits");
        await store.dispatch("fetchPeriodUnitOpts");
      } catch (error) {
        console.error("Error loading reference data:", error);
      }
    };

    watch(allCostsData, (newData) => {
      if (newData?.data && isEdit.value) {
        const cost = newData.data;
        const periodUnitObj = periodUnitOptions.value.find(
          (pu) => pu.value === cost.period_unit
        );

        costObj.value = {
          name: cost.name || "",
          value: cost.value || "",
          unit: cost.unit.pk || null,
          period: cost.period || "",
          period_unit: periodUnitObj.value || null,
        };
      }
    });

    onMounted(() => {
      isEdit.value = !!route.params.pk;
      loadReferenceData();
      if (isEdit.value) {
        loadCostData();
      }
    });

    return {
      canSaveCost,
      allCostsData,
      allCostsCreateData,
      allCostsUpdateData,
      unitsData,
      periodUnitOptsData,
      errorsAdminData,
      isEdit,
      loading,
      costObj,
      unitsOptions,
      periodUnitOptions,
      saveCost,
    };
  },
};
</script>

<style scoped>
.has-error .help-block {
  color: #a94442;
  font-size: 12px;
  margin-top: 5px;
}

.has-error .form-control {
  border-color: #a94442;
}
</style>
